import { Award, Clock, Users, Target, Globe, Shield, Lightbulb, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import teamPhoto from "@/assets/team-photo.jpg";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEO";
import { OrganizationSchema } from "@/components/Schema";

const About = () => {
  const navigate = useNavigate();
  
  const achievements = [
    {
      icon: Award,
      title: "Recognized Solutions",
      description: "Building innovative technology implementations for enterprise clients",
      metric: "Years of Experience"
    },
    {
      icon: Clock,
      title: "Efficient Delivery",
      description: "Fast deployment speeds without compromising quality",
      metric: "Rapid Deployment"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Certified specialists in AI, NetSuite, Shopify, Celigo, and PowerBI",
      metric: "25+ Experts"
    },
    {
      icon: Target,
      title: "Results-Focused",
      description: "Focused on measurable business outcomes for every client",
      metric: "Client Success"
    }
  ];

  const values = [
    {
      icon: Globe,
      title: "Global Excellence",
      description: "Serving clients worldwide with 24/7 support and local expertise"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Built with security-first architecture and compliance standards"
    },
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "Pioneering cutting-edge solutions that shape the future"
    },
    {
      icon: Rocket,
      title: "Rapid Growth",
      description: "Accelerating business transformation through technology"
    }
  ];

  const stats = [
    { label: "Projects Delivered", value: "Many" },
    { label: "Countries Served", value: "Global" },
    { label: "Client Focus", value: "Strong" },
    { label: "Years Experience", value: "10+" }
  ];

  return (
    <>
      <SEOHead
        title="About FortifyTek | Enterprise Technology Partner"
        description="Learn about FortifyTek, an experienced AI and enterprise technology partner. We specialize in NetSuite, Shopify Plus, and AI solutions with a focus on efficient delivery."
        keywords={[
          'About FortifyTek',
          'Technology Partner',
          'Enterprise AI Company',
          'NetSuite Experts',
          'Shopify Plus Agency',
          'Tech Consulting Firm'
        ]}
        canonicalUrl="https://www.fortifytek.com/about"
        schemaType="WebPage"
      />
      <OrganizationSchema />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-40 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight animate-fade-in">
                <span className="block mb-2">
                  Pioneering the Future
                </span>
                <span className="block text-amber-400 pb-2">
                  of Business Technology
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
                A decade of innovation, delivering technology solutions that transform businesses.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-amber-500/50 transition-all duration-300 hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-white/70 text-sm md:text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Content */}
        <section className="py-24 relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="animate-fade-in">
                <h2 className="text-4xl md:text-5xl font-bold mb-8">
                  <span className="text-white">Why Choose </span>
                  <span className="text-amber-400">FortifyTek?</span>
                </h2>
                
                <p className="text-xl text-white/70 mb-8 leading-relaxed">
                  We're not just another tech company. We're your strategic partner in digital transformation, 
                  combining cutting-edge AI technology with deep expertise in enterprise platforms.
                </p>
                
                <p className="text-lg text-white/60 mb-8 leading-relaxed">
                  Our team of certified experts delivers custom solutions that scale with your business. 
                  From AI-powered automation to seamless integrations, we build technology that drives real results.
                </p>

                {/* Team Photo */}
                <div className="mb-8">
                  <img 
                    src={teamPhoto} 
                    alt="FortifyTek Expert Team"
                    className="w-full h-64 object-cover rounded-2xl opacity-80 hover:opacity-100 transition-opacity duration-500"
                  />
                </div>

                <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                  <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                  <p className="text-white/70 leading-relaxed">
                    To empower businesses with effective technology solutions that accelerate growth,
                    improve efficiency, and create competitive advantages in the digital economy through
                    AI, enterprise integrations, and efficient development.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 animate-fade-in delay-300">
                {achievements.map((achievement, index) => (
                  <div
                    key={index}
                    className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-amber-500/50 transition-all duration-300 hover:scale-105 group"
                  >
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-600/10 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                      <achievement.icon className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="text-2xl font-bold text-amber-400 mb-2">{achievement.metric}</div>
                    <h3 className="text-lg font-bold text-white mb-3 group-hover:text-amber-400 transition-colors">
                      {achievement.title}
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      {achievement.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">Our Core </span>
                <span className="text-amber-400">Values</span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                The principles that guide everything we do and drive our commitment to excellence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/50 transition-all duration-500 hover:scale-105 group animate-fade-in"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                    <value.icon className="w-8 h-8 text-amber-500" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors">
                    {value.title}
                  </h3>
                  
                  <p className="text-white/70 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">Ready to </span>
                <span className="text-amber-400">Work Together?</span>
              </h2>
              <p className="text-xl text-white/70 mb-12 max-w-3xl mx-auto">
                Join the ranks of innovative companies who've transformed their business with our expertise.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg"
                  className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                  onClick={() => navigate('/contact')}
                >
                  Start Your Project
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                  onClick={() => navigate('/careers')}
                >
                  Join Our Team
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default About;
